import React from 'react';
import { Box, Typography, Stack, Icons, Chip } from '@curves/ui-base';
import { REDEEM_CART_STATUS } from '@Constants/dataEnums';
import RedeemIcon from '@mui/icons-material/RedeemTwoTone';
import TipIcon from '@mui/icons-material/TipsAndUpdatesTwoTone';

const RedeemItemList = ({ cart = {}, products = [], hasActivity = false, canRedeem = false, description = '', activityPeriod, redeemStatus, redeemStatusText, onEdit }) => {
    // 只取已選（數量 > 0）的商品，並算出已使用點數合計
    const cartItems = products.filter((p) => (cart[p.itemID] || 0) > 0);
    const hasItems = cartItems.length > 0;
    const totalUsedPoints = cartItems.reduce((sum, p) => sum + p.points * cart[p.itemID], 0);
    const isUnredeemed = redeemStatus === REDEEM_CART_STATUS.UNREDEEMED;

    const periodPillClass = `redeem-period-pill${hasActivity ? '' : ' is-inactive'}`;
    const cardClass = `redeem-selected-card${hasActivity ? '' : ' is-inactive'}`;
    const handleCardClick = hasActivity ? onEdit : undefined;

    // 兌換期間文字：有起迄日顯示區間，否則顯示「兌換中」
    const periodText = activityPeriod
        ? `兌換期間：${activityPeriod.startDate} ～ ${activityPeriod.endDate}`
        : '兌換中';

    // 空清單提示文字：已選擇不兌換 vs 尚未選擇
    const emptyHintText = isUnredeemed ? '已選擇不兌換，點此可重新選擇' : '尚未選擇商品，點此開始選擇';

    // 期間標籤內容：無活動只顯示文字，有活動另帶日曆圖示
    const renderPeriodPill = () => {
        if (!hasActivity) {
            return (
                <Typography variant="caption" className="redeem-period-text">
                    目前無兌換活動
                </Typography>
            );
        }
        return (
            <>
                <Icons.CalendarDays className="redeem-period-icon" />
                <Typography variant="caption" className="redeem-period-text">
                    {periodText}
                </Typography>
            </>
        );
    };

    // 卡片內容三態：無活動→空狀態；有活動未選→提示；有活動已選→清單
    const renderCardBody = () => {
        if (!hasActivity) {
            return (
                <Box className="redeem-empty-state">
                    <span role="img" aria-label="禮物" className="redeem-empty-icon"><RedeemIcon /></span>
                    <Typography variant="body2" className="redeem-empty-title">目前無兌換活動</Typography>
                    <Typography variant="caption" className="redeem-empty-desc">
                        活動開始後即可選擇商品進行兌換
                    </Typography>
                </Box>
            );
        }
        if (!hasItems) {
            return (
                <Typography variant="body2" className="redeem-empty-hint">
                    {emptyHintText}
                </Typography>
            );
        }
        return (
            <>
                {cartItems.map((item) => (
                    <Box key={item.itemID} className="redeem-cart-row">
                        <Typography className="redeem-cart-name">{item.itemName}</Typography>
                        <Box className="redeem-cart-right">
                            <Typography className="redeem-cart-qty">× {cart[item.itemID]}</Typography>
                            <Typography className="redeem-cart-subtotal">
                                {item.points * cart[item.itemID]} 點
                            </Typography>
                        </Box>
                    </Box>
                ))}
                <Box className="redeem-cart-total-row">
                    <Typography className="redeem-cart-total-label">共計</Typography>
                    <Typography className="redeem-cart-total-value">{totalUsedPoints} 點</Typography>
                </Box>
            </>
        );
    };

    return (
        <Box className="redeem-item-list">
            <Box className={periodPillClass}>{renderPeriodPill()}</Box>

            {hasActivity && description && (
                <Box className="redeem-activity-desc">
                    <Typography variant="body2" className="redeem-activity-desc-text">
                        <TipIcon /> {description}
                    </Typography>
                </Box>
            )}

            <Box className={cardClass} onClick={handleCardClick}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" className="redeem-card-header">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" className="redeem-card-title">我的兌換選擇</Typography>
                        {isUnredeemed && (
                            <Chip label={redeemStatusText} size="small" color="warning" className="redeem-status-chip" />
                        )}
                    </Stack>
                    {hasActivity && <Typography variant="body2" className="redeem-card-arrow">›</Typography>}
                </Stack>

                <Box className="redeem-card-body">{renderCardBody()}</Box>
            </Box>
        </Box>
    );
};

export default RedeemItemList;
